import React, { CSSProperties } from 'react';
import { Column } from 'react-table';
import { FormattedMessage } from 'react-intl';
import { getYear, getDaysDiff } from '../../../../common/helpers/dateHelper';
import { SortType } from '../../../../common/enums/SortType';
import { IReferenceScore } from '../../../../common/models/reference/IReferenceScore';
import IconButton from '../../../../components/IconButton';
import ScoreStar from '../../../../components/ScoreStar';
import { IBindingCallback1 } from '../../../../common/models/callback';
import Icon from '../../../../components/Icon';
import { SurveyStatus } from '../../../../common/enums/SurveyStatus';
import styles from './styles.module.scss';
import TextOverflowTooltip from '../../../../components/TextOverflowTooltip';
import LinkButton from '../../../../components/LinkButton';
import EmptyFieldPlaceholder from '../../../../components/EmptyFieldPlaceholder';

const respondedStyles = {
  '--badge-color': 'var(--green-600)',
  '--badge-background': 'var(--green-200)'
} as CSSProperties;

const waitingStyles = {
  '--badge-color': 'var(--red-500)',
  '--badge-background': 'var(--red-100)'
} as CSSProperties;

interface IActions {
  onSurveyClick: IBindingCallback1<string>;
  onUpdateStatus: IBindingCallback1<string>;
  onManualInput: IBindingCallback1<string>;
}

export const getColumns = (
  { onSurveyClick, onUpdateStatus, onManualInput }: IActions,
  isActive: boolean
): Column[] => [
  {
    Header: () => <FormattedMessage id="contactProfile.referenceName" defaultMessage="Reference name" />,
    accessor: item => {
      const { lastName, firstName } = item as IReferenceScore;
      return `${lastName}, ${firstName}`;
    },
    id: 'name',
    Cell: ({ cell: { value } }) => (
      <TextOverflowTooltip content={value} width={160}>
        <div className="cell-active">{value}</div>
      </TextOverflowTooltip>
    ),
    sortType: SortType.Literal
  },
  {
    Header: () => <FormattedMessage id="contactProfile.relationship" defaultMessage="Relationship" />,
    id: 'relationship',
    Cell: ({ row: { original } }) => {
      const { type, workEndDate, workStartDate } = original as IReferenceScore;

      if (!workStartDate) {
        return <EmptyFieldPlaceholder />;
      }

      const startYear = `‘${getYear(workStartDate)}`;
      const endYear = workEndDate
        ? `‘${getYear(workEndDate)}`
        : <FormattedMessage id="present" defaultMessage="Present" />;
      return (
        <div>
          {type}
          &nbsp;
          (
          {startYear}
          -
          {endYear}
          )
        </div>
      );
    },
    sortType: SortType.Literal,
    width: 200
  },
  {
    Header: () => <FormattedMessage id="contactProfile.company" defaultMessage="Company" />,
    accessor: 'workInCompany',
    Cell: ({ cell: { value } }) => (value
      ? <TextOverflowTooltip content={value} width={130}>{value}</TextOverflowTooltip>
      : <EmptyFieldPlaceholder />),
    sortType: SortType.Literal,
    width: 130
  },
  {
    Header: () => <FormattedMessage id="contactProfile.surveySent" defaultMessage="Survey sent" />,
    id: 'survey',
    Cell: ({ row: { original } }) => {
      const { surveyId, surveyName } = original as IReferenceScore;
      return (
        <div style={{ maxWidth: '75px' }} className="table-center">
          <IconButton
            onClick={() => onSurveyClick(surveyId)}
            tooltip={surveyName}
            icon={<Icon icon="survey" />}
            variant="blue"
            size="lg"
          />
        </div>
      );
    },
    width: 110
  },
  {
    Header: () => <FormattedMessage id="status" defaultMessage="Status" />,
    id: 'status',
    Cell: ({ row: { original } }) => {
      const { surveySendDate, status, id } = original as IReferenceScore;

      switch (status) {
        case SurveyStatus.Finished:
          return (
            <div
              style={isActive ? respondedStyles : undefined}
              className={`custom-badge ${styles.statusBadge}`}
            >
              <Icon icon="check" />
              <FormattedMessage id="responded" defaultMessage="Responded" />
            </div>
          );
        case SurveyStatus.Pending:
          return (
            <LinkButton
              textIntlId="reviewNow"
              defaultMessage="Review now"
              onClick={() => onUpdateStatus(id)}
            />
          );
        case SurveyStatus.ManualInput:
          return (
            <LinkButton
              textIntlId="manualInput"
              defaultMessage="Manual input"
              onClick={() => onManualInput(id)}
            />
          );
        default:
          const daysWaiting = getDaysDiff(surveySendDate);
          return (
            <div
              style={(isActive && daysWaiting >= 5) ? waitingStyles : undefined}
              className={`custom-badge ${styles.statusBadge}`}
            >
              {isActive
                ? (
                  <FormattedMessage
                    id={`referenceStatus.${daysWaiting > 1 ? 'waitingMany' : 'waitingOne'}`}
                    defaultMessage="Waiting {value} day(s)"
                    values={{ value: daysWaiting < 5 ? daysWaiting : '5+' }}
                  />
                )
                : <FormattedMessage id="referenceStatus.didNotRespond" defaultMessage="Did not respond" />}
            </div>
          );
      }
    },
    width: 200,
    sortType: SortType.Numeric
  },
  {
    Header: () => <FormattedMessage id="contactProfile.score" defaultMessage="Score" />,
    accessor: 'score',
    Cell: ({ cell: { value } }) => (
      <div className="table-center">
        <ScoreStar score={value} />
      </div>
    ),
    width: 80,
    sortType: SortType.Numeric
  }
];
